import { createElement, type CSSProperties } from "react";

import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import { colors } from "@rubi/ui";
import ReactDOMServer from "react-dom/server";
import { ElementFactory, Question, Serializer, SvgRegistry } from "survey-core";
import { localization } from "survey-creator-core";
import { PropertyGridEditorCollection } from "survey-creator-react";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

export const GAS_QUANTITY_INPUT_TYPE = "gas-quantity-input";

export class QuestionGasQuantityInputModel extends Question {
  getType() {
    return GAS_QUANTITY_INPUT_TYPE;
  }
}

// Guard against duplicate registration (can happen with HMR or multiple imports)
export function registerGasQuantityInput() {
  if (
    !ElementFactory.Instance.getAllTypes().includes(GAS_QUANTITY_INPUT_TYPE)
  ) {
    ElementFactory.Instance.registerElement(GAS_QUANTITY_INPUT_TYPE, (name) => {
      return new QuestionGasQuantityInputModel(name);
    });
  }
}

// Specify display names for the question type
const locale = localization.getLocale("");
locale.qt[GAS_QUANTITY_INPUT_TYPE] = "Gas Quantity Input";

const SURVEY_ICON_COLOR = colors.text.disabled;
const svg = ReactDOMServer.renderToString(
  <LocalGasStationIcon style={{ fill: SURVEY_ICON_COLOR }} />,
);
SvgRegistry.registerIconFromSvg(GAS_QUANTITY_INPUT_TYPE, svg);

// Guard against duplicate registration (can happen with HMR or multiple imports)
if (!Serializer.findClass(GAS_QUANTITY_INPUT_TYPE)) {
  Serializer.addClass(
    GAS_QUANTITY_INPUT_TYPE,
    [],
    function () {
      return new QuestionGasQuantityInputModel("");
    },
    "question",
  );
}

export class SurveyQuestionGasQuantityInput extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    this.state = {
      displayValue: this.question.value || "",
    };
  }

  componentDidUpdate(prevProps: any) {
    if (this.question.value !== prevProps.question?.value) {
      this.setState({ displayValue: this.question.value || "" });
    }
  }

  get question() {
    return this.questionBase;
  }

  get value() {
    return this.question.value;
  }

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const numericValue = inputValue.replace(/[^\d.]/g, "");

    this.question.value = numericValue;
    this.setState({ displayValue: numericValue });
  };

  get style(): CSSProperties | undefined {
    return this.question.getPropertyValue("readOnly") ||
      this.question.isDesignMode
      ? { pointerEvents: "none" }
      : undefined;
  }

  renderElement() {
    return (
      <div
        style={{ position: "relative", display: "inline-block", width: "100%" }}
      >
        <input
          className="sd-input sd-text"
          type="text"
          inputMode="decimal"
          value={this.state.displayValue}
          onChange={this.handleChange}
          disabled={this.props.isDisplayMode || this.question.isReadOnly}
          placeholder="0"
          style={{
            ...this.style,
            paddingRight: "30px",
          }}
        />
        <span
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            // Muted unit label
            color: "rgba(144, 144, 144, 1)",
          }}
        >
          L
        </span>
      </div>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(
  GAS_QUANTITY_INPUT_TYPE,
  (props) => {
    return createElement(SurveyQuestionGasQuantityInput, props);
  },
);

// Register property grid editor
PropertyGridEditorCollection.register({
  fit: function (prop: { type: string }) {
    return prop.type === "gas-quantity";
  },
  getJSON: function () {
    return {
      type: GAS_QUANTITY_INPUT_TYPE,
    };
  },
});
